import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Keeps how a listing page was filtered across a trip to its Add/Edit Form.
 *
 * Two things are remembered, both only for the round trip: the criteria of the search the user
 * *ran* in the search panel, and the grid's filtering (filter row, header filters, filter panel
 * expression, grid search, page).
 *
 * **Layout and filtering live in different places.** A grid layout - which columns, in what order,
 * how wide, sorted how - is a durable, shareable preference and belongs in the database, which is
 * where `GridLayout` puts it. A filter is one person's transient view of one tab right now, so it
 * is tracked here in sessionStorage and never sent to the server. That split is also what makes
 * the restore robust: `loadGridState` loads the layout from the database and overlays the tracked
 * filtering on top, so it answers every call identically - and DevExtreme asks far more often than
 * once.
 *
 * An entry lives for exactly one round trip. Navigating anywhere other than the page's own detail
 * routes deletes it, a reload deletes it, and logging out deletes every page's.
 *
 * Full rationale, and the DevExtreme behaviour that forces this shape, is in `Architecture.md`
 * rule 17. Read that before changing `loadGridState` or `saveGridState`.
 */

// ---------------------------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------------------------

const STORAGE_PREFIX = 'argos.listSearchState.';

// Bump when the shape of a stored entry changes, so entries written by an older build are ignored
// rather than read with a shape that no longer matches.
const STORAGE_VERSION = 4;

const storageKeyFor = (pageKey) => STORAGE_PREFIX + pageKey;

/**
 * Identity of this page load, stamped into every entry so a reload can be told from an in-app
 * navigation - sessionStorage survives F5, and a reload is a request for a fresh page.
 *
 * It lives on `window`, not in module scope. A module-level marker would also be wiped by a
 * webpack hot reload, which re-evaluates modules without reloading the document, and every
 * remembered filter would vanish on the next Back for as long as the dev server was rebuilding.
 */
const pageLoadId = (() => {
    if (typeof window === 'undefined') return 'no-window';
    if (!window.__argosPageLoadId) {
        window.__argosPageLoadId = Date.now() + '.' + Math.random().toString(36).slice(2);
    }
    return window.__argosPageLoadId;
})();

/**
 * Opt-in tracing. Run `window.__argosSearchStateDebug = true` in the console, then reproduce.
 * Free when off, and "which branch ran" is the first question whenever a restore misbehaves.
 */
const trace = (pageKey, event, detail) => {
    if (typeof window !== 'undefined' && window.__argosSearchStateDebug) {
        // eslint-disable-next-line no-console
        console.log('[searchState:' + pageKey + '] ' + event, detail);
    }
};

// sessionStorage throws rather than returning null in a few real browser states (Safari private
// mode, storage disabled by policy, quota exhausted). Remembering a search is a convenience, so
// every access degrades to "nothing stored" instead of breaking the page.
function readSearchState(pageKey) {
    if (!pageKey) return null;
    try {
        const raw = sessionStorage.getItem(storageKeyFor(pageKey));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && parsed.version === STORAGE_VERSION ? parsed : null;
    } catch (err) {
        return null;
    }
}

/** Merges patch into the entry already stored, so writers touch only their own keys. */
function saveSearchState(pageKey, patch) {
    if (!pageKey) return;
    try {
        const next = {
            ...(readSearchState(pageKey) || {}),
            ...patch,
            version: STORAGE_VERSION,
            pageLoadId,
        };
        sessionStorage.setItem(storageKeyFor(pageKey), JSON.stringify(next));
    } catch (err) {
        // Nothing to recover: the search still runs, it just will not be remembered.
    }
}

function clearSearchState(pageKey) {
    if (!pageKey) return;
    try {
        sessionStorage.removeItem(storageKeyFor(pageKey));
    } catch (err) {
        // ignored - see readSearchState
    }
}

/** Drops every page's entry. Call on logout, so nothing crosses user sessions. */
export function clearAllSearchStates() {
    try {
        Object.keys(sessionStorage)
            .filter((key) => key.indexOf(STORAGE_PREFIX) === 0)
            .forEach((key) => sessionStorage.removeItem(key));
    } catch (err) {
        // ignored - see readSearchState
    }
}

// ---------------------------------------------------------------------------------------------
// Search panel criteria
// ---------------------------------------------------------------------------------------------

/**
 * Names of the date/datetime criteria in a search layout. Accepts both shapes buildDxFilters
 * accepts: an array of field groups, or a flat { fieldName: type } map.
 */
function collectDateFieldNames(layout) {
    const names = [];
    if (!layout) return names;
    if (Array.isArray(layout)) {
        layout.forEach((group) => {
            ((group && group.fields) || []).forEach((field) => {
                const type = field.filterType ?? field.type;
                if (type === 'date' || type === 'datetime') names.push(field.name);
            });
        });
    } else {
        Object.entries(layout).forEach(([name, type]) => {
            if (type === 'date' || type === 'datetime') names.push(name);
        });
    }
    return names;
}

/**
 * Rebuilds a page's form fields from a stored entry. The page's own initial fields decide the
 * shape, so a criterion the layout no longer declares is not resurrected and one it gained keeps
 * its initial value. Date criteria are revived from the ISO strings JSON turns them into, because
 * a DateBox only displays a value it recognises.
 */
function mergeStoredFields(storedFields, initialFormFields, dateFieldNames) {
    const merged = { ...initialFormFields };
    if (!storedFields) return merged;

    Object.keys(initialFormFields).forEach((name) => {
        if (!Object.prototype.hasOwnProperty.call(storedFields, name)) return;
        const value = storedFields[name];
        if (dateFieldNames.indexOf(name) !== -1 && typeof value === 'string' && value) {
            const revived = new Date(value);
            merged[name] = isNaN(revived.getTime()) ? null : revived;
            return;
        }
        merged[name] = value;
    });
    return merged;
}

/** True when path is one of paths, or a route nested under one of them. */
function isPathIn(path, paths) {
    if (!path || !paths || paths.length === 0) return false;
    return paths.some((candidate) => path === candidate || path.indexOf(candidate + '/') === 0);
}

// ---------------------------------------------------------------------------------------------
// Grid: telling filtering apart from layout
//
// One list of filtering keys, taken from DevExtreme's own USER_STATE_FIELD_NAMES
// (grid_core/columns_controller/const.js) and getDataState (grid_core/state_storing). Everything
// not on it is layout. `stripGridFilters` is the single source of truth for that boundary: it
// produces the payload sent to the database AND the fingerprint used to decide whether to send it,
// so the two cannot drift.
// ---------------------------------------------------------------------------------------------

const COLUMN_FILTER_KEYS = [
    'filterValue', 'bufferedFilterValue', 'selectedFilterOperation',
    'bufferedSelectedFilterOperation', 'filterValues', 'filterType',
];

const STATE_FILTER_KEYS = [
    'filterValue', 'filterPanel', 'searchText', 'pageIndex',
    'selectedRowKeys', 'selectionFilter', 'focusedRowKey',
];

// DevExtreme's applyFieldsState assigns most column fields unconditionally - so a field missing
// from the state we hand back is *cleared* - but assigns these only `if (key in userStateColumn)`.
// Leaving them out therefore preserves whatever is already on the live column, which is the
// opposite of what we want. They have to be emitted explicitly, null when nothing filters.
const DENSE_COLUMN_FILTER_KEYS = ['filterValues', 'filterType'];

const isSet = (value) => value !== null && value !== undefined && value !== '';
const isNonEmptyList = (value) => Array.isArray(value) && value.length > 0;

/** A copy of a grid state with all filtering removed, leaving only the layout. */
function stripGridFilters(state) {
    if (!state) return state;
    const layout = { ...state };
    STATE_FILTER_KEYS.forEach((key) => delete layout[key]);
    layout.columns = (state.columns || []).map((column) => {
        const kept = { ...column };
        COLUMN_FILTER_KEYS.forEach((key) => delete kept[key]);
        return kept;
    });
    return layout;
}

/** JSON with object keys sorted, so two equal states always produce the same string. */
function canonicalJson(value) {
    if (Array.isArray(value)) return '[' + value.map(canonicalJson).join(',') + ']';
    if (value && typeof value === 'object') {
        return '{' + Object.keys(value).sort()
            .filter((key) => value[key] !== undefined)
            .map((key) => JSON.stringify(key) + ':' + canonicalJson(value[key]))
            .join(',') + '}';
    }
    return JSON.stringify(value) ?? 'null';
}

/** Identity of the layout half of a grid state - what the database actually stores. */
const gridLayoutFingerprint = (state) => (state ? canonicalJson(stripGridFilters(state)) : null);

/**
 * The filtering in a grid state, keyed by column `dataField` so it survives a reorder.
 *
 * The record is **sparse**: a key is present only when it actually filters. That keeps the stored
 * entry small on a 200-column grid and makes "does anything filter?" a simple emptiness check.
 */
function extractGridFilters(state) {
    if (!state) return null;

    const columns = {};
    (state.columns || []).forEach((column) => {
        const key = column.dataField || column.name;
        if (!key) return;
        const kept = {};
        if (isSet(column.filterValue)) {
            kept.filterValue = column.filterValue;
            if (column.selectedFilterOperation) {
                kept.selectedFilterOperation = column.selectedFilterOperation;
            }
        }
        if (isNonEmptyList(column.filterValues)) {
            kept.filterValues = column.filterValues;
            if (column.filterType) kept.filterType = column.filterType;
        }
        if (Object.keys(kept).length > 0) columns[key] = kept;
    });

    const tracked = { columns };
    if (isSet(state.filterValue)) tracked.filterValue = state.filterValue;
    if (state.searchText) tracked.searchText = state.searchText;
    if (state.pageIndex) tracked.pageIndex = state.pageIndex;
    // Only worth recording when the user has switched the filter panel off, since on is the default.
    if (state.filterPanel && state.filterPanel.filterEnabled === false) tracked.filterEnabled = false;
    return tracked;
}

/** True when tracked filtering actually filters something. */
function hasAnyGridFilter(tracked) {
    if (!tracked) return false;
    if (tracked.filterValue !== undefined || tracked.searchText) return true;
    return Object.keys(tracked.columns || {}).length > 0;
}

/**
 * Lays tracked filtering over a layout from the database, producing the state to hand DevExtreme.
 *
 * The overlay owns filtering completely: it starts from the stripped layout so filter remnants
 * already sitting in older database rows cannot re-apply a filter the user cleared, and it emits
 * the dense keys explicitly for the reason given on DENSE_COLUMN_FILTER_KEYS.
 */
function applyGridFilters(layoutState, tracked) {
    const layout = stripGridFilters(layoutState);
    if (!layout) return layoutState;

    const byField = (tracked && tracked.columns) || {};
    layout.columns = (layout.columns || []).map((column) => {
        const key = column.dataField || column.name;
        const kept = (key && byField[key]) || {};
        const merged = { ...column, ...kept };
        DENSE_COLUMN_FILTER_KEYS.forEach((dense) => {
            merged[dense] = kept[dense] ?? null;
        });
        return merged;
    });

    // Left absent, never null: with filterSync on, an authoritative-but-empty grid expression can
    // clear the column filters, whereas absent lets DevExtreme derive it from the columns.
    if (tracked && tracked.filterValue !== undefined) layout.filterValue = tracked.filterValue;
    layout.searchText = (tracked && tracked.searchText) || '';
    layout.pageIndex = (tracked && tracked.pageIndex) || 0;
    layout.filterPanel = { filterEnabled: !(tracked && tracked.filterEnabled === false) };
    return layout;
}

// ---------------------------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------------------------

/**
 * Usage on a listing page:
 *
 *   const { formFields, setformFields, rememberSearch, forgetSearch,
 *           loadGridState, saveGridState } = useSearchState({
 *       pageKey: SEARCH_STATE_PAGE_KEY,
 *       initialFormFields,
 *       layout: SearchLayout,
 *       detailPaths: SEARCH_STATE_DETAIL_PATHS,
 *       enabled: !props.pickerMode,
 *       gridRef: dataGrid,
 *       loadGridLayoutState: loadState,
 *       saveGridLayoutState: saveState,
 *       gridLayoutName: currentLayout,
 *   });
 *
 * `formFields` already carries the restored criteria on the first render, so the page's existing
 * mount effect re-applies the remembered search on its normal first pass - no extra fetch, no
 * flash of unfiltered rows. Call `rememberSearch(formFields)` from Search and `forgetSearch()`
 * from Reset, and hand `loadGridState` / `saveGridState` to StateStoring.
 *
 * @param {string}       pageKey               Stable id for the page. Unique across the app.
 * @param {object}       initialFormFields     The page's empty criteria; defines the restored shape.
 * @param {Array|object} [layout]              Search layout, used to revive date criteria.
 * @param {string[]}     [detailPaths]         Routes the entry survives; omit to keep it for the
 *                                             whole tab session.
 * @param {boolean}      [enabled=true]        false where the listing is reused transiently (a
 *                                             record-picker popup), so it never touches storage.
 *                                             Note this does NOT disable the layout-POST guard.
 * @param {object}       [gridRef]             Ref to the DataGrid, read on the way into a detail
 *                                             route to catch filtering inside the save debounce.
 * @param {Function}     [loadGridLayoutState] The page's own StateStoring customLoad.
 * @param {Function}     [saveGridLayoutState] The page's own StateStoring customSave.
 * @param {string}       [gridLayoutName]      The selected named layout; a change to it drops the
 *                                             tracked filtering.
 */
export function useSearchState({
    pageKey,
    initialFormFields,
    layout,
    detailPaths,
    enabled = true,
    gridRef,
    loadGridLayoutState,
    saveGridLayoutState,
    gridLayoutName,
}) {
    const history = useHistory();
    const dateFieldNames = useMemo(() => collectDateFieldNames(layout), [layout]);

    // The page rebuilds its initial-fields literal every render; the first one is the contract.
    const initialFieldsRef = useRef(initialFormFields);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    // The filtering tracked for this grid, plus the last values written / posted, so an unchanged
    // half can be skipped. Read through refs because the departure listener registers once.
    const gridFiltersRef = useRef(null);
    const writtenFiltersRef = useRef(null);
    const postedLayoutRef = useRef(null);
    const servedLayoutRef = useRef(gridLayoutName);
    const gridLayoutNameRef = useRef(gridLayoutName);
    gridLayoutNameRef.current = gridLayoutName;

    // Read storage during the first render, not in an effect: the page's mount effect builds the
    // DataSource from formFields, and StateStoring asks for grid state while the grid initialises.
    const restoredRef = useRef(undefined);
    if (restoredRef.current === undefined) {
        // A disabled instance - a picker popup sharing this page key - reads nothing and, just as
        // importantly, discards nothing: it must not touch the full page's entry.
        let entry = enabled ? readSearchState(pageKey) : null;
        if (entry) {
            // An entry stamped with a different page load outlived a reload, and a reload asks for
            // a fresh page. Otherwise the route the user left for says whether this is a round trip
            // or a fresh arrival - a backstop for when the departure listener never ran.
            const staleAfterReload = entry.pageLoadId !== pageLoadId;
            const arrivedFromElsewhere = detailPaths && detailPaths.length
                && !isPathIn(entry.leftTo, detailPaths);
            if (staleAfterReload || arrivedFromElsewhere) {
                trace(pageKey, 'restore: discarding entry', { staleAfterReload, arrivedFromElsewhere });
                clearSearchState(pageKey);
                entry = null;
            }
        }
        restoredRef.current = entry;

        // Filtering is scoped to the layout it was set on: switching layout is a deliberate "show
        // me this view", and a filter on a column that view hides means nothing.
        const sameLayout = !!entry
            && (entry.gridLayoutName === undefined || entry.gridLayoutName === gridLayoutName);
        gridFiltersRef.current = (entry && sameLayout && entry.gridFilters) || null;
        writtenFiltersRef.current = canonicalJson(gridFiltersRef.current);

        trace(pageKey, 'restore', {
            enabled,
            restored: !!entry,
            leftTo: entry && entry.leftTo,
            hasFields: !!(entry && entry.fields),
            hasFilters: hasAnyGridFilter(gridFiltersRef.current),
        });
    }
    const restored = restoredRef.current;

    const [formFields, setformFields] = useState(() => (
        restored
            ? mergeStoredFields(restored.fields, initialFieldsRef.current, dateFieldNames)
            : initialFieldsRef.current
    ));

    // Backstop for the save debounce: DevExtreme's dispose() clears a pending save rather than
    // flushing it, so filtering applied in the last few hundred ms before Edit would be lost.
    const captureGridFilters = () => {
        const grid = gridRef && gridRef.current;
        if (!grid || typeof grid.instance !== 'function') return null;
        try {
            return extractGridFilters(grid.instance().state());
        } catch (err) {
            return null; // grid already disposed - the tracked copy stands
        }
    };

    // Watch where the user goes while the listing is mounted: into one of its own detail routes is
    // the round trip the entry exists for, anywhere else ends its life.
    useEffect(() => {
        if (!enabled || !pageKey) return undefined;

        const ownPath = history.location && history.location.pathname;

        const recordDeparture = (pathname) => {
            // Still on the listing means an unmount with no navigation (a remount, a hot reload),
            // which is not the user leaving and must not delete their filters.
            if (!pathname || pathname === ownPath) return;

            if (!detailPaths || !detailPaths.length || isPathIn(pathname, detailPaths)) {
                const captured = captureGridFilters();
                // Nothing searched and nothing filtered means there is nothing to come back to.
                if (!readSearchState(pageKey) && !hasAnyGridFilter(captured)) return;
                const patch = { leftTo: pathname };
                if (captured) {
                    patch.gridFilters = captured;
                    patch.gridLayoutName = gridLayoutNameRef.current;
                }
                saveSearchState(pageKey, patch);
                return;
            }
            clearSearchState(pageKey);
        };

        // A reload asks for a fresh page, so take the entry with it. The pageLoadId stamp already
        // makes the restore ignore it; this just stops it lingering in devtools.
        const onPageHide = () => clearSearchState(pageKey);
        window.addEventListener('pagehide', onPageHide);
        const unlisten = history.listen((location) => recordDeparture(location && location.pathname));

        return () => {
            unlisten();
            window.removeEventListener('pagehide', onPageHide);
            // Whether the listener fired before this teardown depends on how React batched the
            // click that navigated; by now history.location is already the destination.
            recordDeparture(history.location && history.location.pathname);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, pageKey, history, detailPaths]);

    /** Tracks the grid's filtering. Skips the write when only selection or layout moved. */
    const rememberFilters = (state) => {
        let tracked;
        try {
            tracked = extractGridFilters(state);
        } catch (err) {
            return false;
        }
        const key = canonicalJson(tracked);
        if (key === writtenFiltersRef.current) return false;
        writtenFiltersRef.current = key;
        gridFiltersRef.current = tracked;
        // Only create an entry once there is something to come back to, or every listing in the app
        // would get one on first paint. An entry that exists keeps updating, so clearing is recorded.
        if (hasAnyGridFilter(tracked) || readSearchState(pageKey)) {
            saveSearchState(pageKey, {
                gridFilters: tracked,
                gridLayoutName: gridLayoutNameRef.current,
            });
        }
        return true;
    };

    /**
     * Sends the layout to the database, but only when the layout actually changed.
     *
     * DevExtreme hands customSave the whole state and cannot say what changed, and it fires on
     * selection changes and on every filter change as well as on real layout edits. Comparing the
     * stripped payload against the last one sent is what keeps those off the wire. The fingerprint
     * is updated at send time, not on success, so the duplicate flush DevExtreme performs on
     * `visibilitychange` collapses into one request.
     */
    const postLayoutIfChanged = (state) => {
        if (!saveGridLayoutState) return undefined;
        const fingerprint = gridLayoutFingerprint(state);
        if (fingerprint === postedLayoutRef.current) return undefined;
        postedLayoutRef.current = fingerprint;
        return saveGridLayoutState(stripGridFilters(state));
    };

    /** StateStoring customSave. */
    const saveGridState = useCallback((state) => {
        if (!state) return undefined;
        // The database half runs whatever `enabled` says: it is about keeping needless requests off
        // the wire, and a picker popup - which is nothing but selection changes - shares this key.
        const filtersChanged = enabledRef.current ? rememberFilters(state) : false;
        const before = postedLayoutRef.current;
        const result = postLayoutIfChanged(state);
        trace(pageKey, 'saveGridState', {
            filtersChanged,
            layoutPosted: postedLayoutRef.current !== before,
        });
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageKey, saveGridLayoutState]);

    /**
     * StateStoring customLoad. The layout comes from the database, the filtering from here.
     *
     * It answers every call the same way rather than restoring once, because DevExtreme re-reads
     * state on any `stateStoring.*` option change and something in a live page is always touching
     * one. Idempotence is what stops a re-read wiping the filters a moment after they appear.
     */
    const loadGridState = useCallback(() => {
        if (gridLayoutName !== servedLayoutRef.current) {
            servedLayoutRef.current = gridLayoutName;
            gridFiltersRef.current = null;
            writtenFiltersRef.current = canonicalJson(null);
            if (enabledRef.current && readSearchState(pageKey)) {
                saveSearchState(pageKey, { gridFilters: null, gridLayoutName });
            }
        }

        const loading = loadGridLayoutState ? loadGridLayoutState() : Promise.resolve(null);
        return Promise.resolve(loading).then((layoutState) => {
            // Seed the fingerprint from what the database returned, so the grid echoing that same
            // layout straight back is not mistaken for a change.
            postedLayoutRef.current = gridLayoutFingerprint(layoutState);
            const tracked = gridFiltersRef.current;
            trace(pageKey, 'loadGridState', {
                gotLayout: !!layoutState,
                hasFilters: hasAnyGridFilter(tracked),
            });
            return applyGridFilters(layoutState, tracked);
        });
    }, [pageKey, loadGridLayoutState, gridLayoutName]);

    /** Remembers the criteria of the search just run. Call from the page's Search handler. */
    const rememberSearch = useCallback((fields) => {
        if (!enabledRef.current) return;
        saveSearchState(pageKey, { fields });
    }, [pageKey]);

    /**
     * Clears the remembered search and returns the page's initial criteria, so the caller can
     * rebuild the grid's filter without waiting for the state update to land.
     */
    const forgetSearch = useCallback(() => {
        if (enabledRef.current) clearSearchState(pageKey);
        const initialFields = initialFieldsRef.current;
        setformFields(initialFields);
        return initialFields;
    }, [pageKey]);

    return {
        formFields,
        setformFields,
        rememberSearch,
        forgetSearch,
        loadGridState,
        saveGridState,
    };
}
